import { useState, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Wind, Heart, Zap, ArrowLeft, Lightbulb, Moon, BedDouble, Sparkles as SparklesIcon } from "lucide-react";
import { Link } from "react-router-dom";
import calmBackground from "@/assets/calm-background.jpg";
import { BreathingSession } from "@/components/BreathingSession";
import { GroundingExercise } from "@/components/GroundingExercise";
import { FocusBoost } from "@/components/FocusBoost";
import { SleepPrep } from "@/components/SleepPrep";
import { FourSevenEightBreathing } from "@/components/FourSevenEightBreathing";
import { MuscleRelaxation } from "@/components/MuscleRelaxation";

const tools = [
  {
    id: "breathing",
    title: "Cooling Breath",
    description: "Guided breathing to ease hot flashes and anxiety",
    icon: Wind,
    duration: "3 min",
  },
  {
    id: "grounding",
    title: "Grounding Exercise",
    description: "Quick meditation for overwhelm and stress",
    icon: Heart,
    duration: "5 min",
  },
  {
    id: "focus",
    title: "Focus Boost",
    description: "Interactive game to combat brain fog",
    icon: Zap,
    duration: "2 min",
  },
  {
    id: "sleep-prep",
    title: "Sleep Preparation",
    description: "10-minute wind-down checklist for better sleep",
    icon: Moon,
    duration: "10 min",
  },
  {
    id: "478-breathing",
    title: "4-7-8 Sleep Breath",
    description: "Breathing pattern proven to induce drowsiness",
    icon: Wind,
    duration: "5 min",
  },
  {
    id: "muscle-relaxation",
    title: "Muscle Relaxation",
    description: "Guided tension-release for deep rest",
    icon: SparklesIcon,
    duration: "15 min",
  }
];

const SOSToolkit = () => {
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [showBreathingSession, setShowBreathingSession] = useState(false);
  const [showGroundingExercise, setShowGroundingExercise] = useState(false);
  const [showFocusBoost, setShowFocusBoost] = useState(false);
  const [showSleepPrep, setShowSleepPrep] = useState(false);
  const [show478Breathing, setShow478Breathing] = useState(false);
  const [showMuscleRelaxation, setShowMuscleRelaxation] = useState(false);
  const exerciseRef = useRef<HTMLDivElement>(null);

  // Scroll to exercise when a tool is selected
  useEffect(() => {
    if (selectedTool && exerciseRef.current) {
      setTimeout(() => {
        exerciseRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [selectedTool]);

  return (
    <>
      {showBreathingSession && (
        <BreathingSession onClose={() => setShowBreathingSession(false)} />
      )}
      
      {showGroundingExercise && (
        <GroundingExercise onClose={() => setShowGroundingExercise(false)} />
      )}

      {showFocusBoost && (
        <FocusBoost onClose={() => setShowFocusBoost(false)} />
      )}

      {showSleepPrep && (
        <SleepPrep onClose={() => setShowSleepPrep(false)} />
      )}

      {show478Breathing && (
        <FourSevenEightBreathing onClose={() => setShow478Breathing(false)} />
      )}

      {showMuscleRelaxation && (
        <MuscleRelaxation onClose={() => setShowMuscleRelaxation(false)} />
      )}
      
      <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 py-12 md:py-16">
        {/* Header */}
        <div className="max-w-4xl mx-auto mb-12">
          <Link to="/">
            <Button variant="ghost" className="gap-2 mb-6 hover:bg-accent/50">
              <ArrowLeft className="h-4 w-4" />
              Back Home
            </Button>
          </Link>
          <h1 className="font-headline text-5xl md:text-6xl mb-6 text-foreground leading-tight">
            Immediate Relief Toolkit
          </h1>
          <p className="font-serif text-xl md:text-2xl text-muted-foreground leading-relaxed">
            Evidence-based techniques for symptom management. Select an intervention that aligns with your current needs.
          </p>
        </div>

        {/* Tools Grid */}
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <Card 
                key={tool.id} 
                className="group relative overflow-hidden bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/30 hover:shadow-elegant transition-all duration-500 cursor-pointer"
                onClick={() => setSelectedTool(tool.id)}
              >
                <div className="p-8">
                  <div className="mb-6 flex items-center justify-between">
                    <div className="p-3 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <span className="font-serif text-sm text-muted-foreground">{tool.duration}</span>
                  </div>
                  
                  <h3 className="font-headline text-2xl mb-3 text-foreground group-hover:text-primary transition-colors">
                    {tool.title}
                  </h3>
                  
                  <p className="font-serif text-muted-foreground leading-relaxed mb-6">
                    {tool.description}
                  </p>
                  
                  <div className="flex items-center text-sm font-medium text-primary group-hover:gap-2 transition-all">
                    Begin Session
                    <ArrowLeft className="h-4 w-4 rotate-180 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                
                {/* Subtle gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
              </Card>
            );
          })}
        </div>

        {/* Breathing Exercise Details - Only show when clicked */}
        {selectedTool === "breathing" && (
          <Card ref={exerciseRef} className="max-w-4xl mx-auto bg-card/80 backdrop-blur-sm border-border/50 shadow-elegant p-8 md:p-12 mb-16 scroll-mt-4">
            <div>
              <Button
                variant="ghost"
                onClick={() => setSelectedTool(null)}
                className="mb-8 gap-2 hover:bg-accent/50"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Tools
              </Button>
              
              <div>
                <h2 className="font-headline text-4xl md:text-5xl mb-6 text-foreground">
                  Quick Cooling Breath
                </h2>
                <p className="font-serif text-xl text-muted-foreground leading-relaxed mb-8">
                  This simple breathing pattern can help reduce hot flash intensity and calm your nervous system.
                </p>
                
                {/* Why It Works Section */}
                <div className="bg-accent/10 border-l-4 border-primary rounded-lg p-6 md:p-8 mb-10">
                  <h3 className="font-headline text-2xl mb-4 text-foreground flex items-center gap-3">
                    <Lightbulb className="h-6 w-6 text-primary" />
                    Clinical Evidence
                  </h3>
                  <p className="font-serif text-muted-foreground leading-relaxed text-lg">
                    Slow, deep breathing activates your parasympathetic nervous system - your body's natural cooling and calming response. This helps regulate your body's temperature control center and reduces the intensity of hot flashes. Research shows paced breathing at 6-8 breaths per minute can reduce hot flash frequency by 40-50%.
                  </p>
                </div>
                
                {/* Breathing Circle Animation */}
                <div className="flex justify-center mb-10">
                  <div className="relative">
                    <div className="w-56 h-56 rounded-full bg-primary/10 animate-breathe flex items-center justify-center">
                      <div className="w-40 h-40 rounded-full bg-primary/20 flex items-center justify-center">
                        <Wind className="h-14 w-14 text-primary" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-5 bg-background/50 backdrop-blur-sm p-8 rounded-lg border border-border/50 mb-10">
                  <div className="flex gap-5 items-start">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline text-lg">
                      1
                    </span>
                    <p className="font-serif text-foreground text-lg pt-1">Breathe in slowly through your nose for 4 counts</p>
                  </div>
                  <div className="flex gap-5 items-start">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline text-lg">
                      2
                    </span>
                    <p className="font-serif text-foreground text-lg pt-1">Hold gently for 2 counts</p>
                  </div>
                  <div className="flex gap-5 items-start">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline text-lg">
                      3
                    </span>
                    <p className="font-serif text-foreground text-lg pt-1">Exhale slowly through pursed lips for 6 counts</p>
                  </div>
                  <div className="flex gap-5 items-start">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline text-lg">
                      4
                    </span>
                    <p className="font-serif text-foreground text-lg pt-1">Repeat for 3-5 minutes or until you feel relief</p>
                  </div>
                </div>

                <div className="text-center">
                  <Button type="button" size="lg" className="shadow-elegant px-8" onClick={() => setShowBreathingSession(true)}>
                    Begin Guided Session
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Grounding Exercise Details - Only show when clicked */}
        {selectedTool === "grounding" && (
          <Card ref={exerciseRef} className="max-w-4xl mx-auto bg-card/80 backdrop-blur-sm border-border/50 shadow-elegant p-8 md:p-12 mb-16 scroll-mt-4">
            <div>
              <Button
                variant="ghost"
                onClick={() => setSelectedTool(null)}
                className="mb-8 gap-2 hover:bg-accent/50"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Tools
              </Button>
              
              <div>
                <h2 className="font-headline text-4xl md:text-5xl mb-6 text-foreground">
                  5-4-3-2-1 Grounding Exercise
                </h2>
                <p className="font-serif text-xl text-muted-foreground leading-relaxed mb-8">
                  A powerful sensory technique to bring you back to the present moment when feeling overwhelmed or anxious.
                </p>
                
                {/* Why It Works Section */}
                <div className="bg-accent/10 border-l-4 border-primary rounded-lg p-6 md:p-8 mb-10">
                  <h3 className="font-headline text-2xl mb-4 text-foreground flex items-center gap-3">
                    <Lightbulb className="h-6 w-6 text-primary" />
                    Clinical Evidence
                  </h3>
                  <p className="font-serif text-muted-foreground leading-relaxed text-lg mb-4">
                    When you're anxious, overwhelmed, or experiencing a hot flash, your mind is often stuck in the past or future. This grounding technique uses your five senses to anchor you firmly in the present moment.
                  </p>
                  <p className="font-serif text-muted-foreground leading-relaxed text-lg">
                    By consciously engaging each sense, you interrupt the stress response and activate your parasympathetic nervous system - your body's natural calming mechanism. This shifts your focus from internal anxiety to external reality, giving your nervous system permission to relax.
                  </p>
                </div>

                <div className="space-y-5 bg-background/50 backdrop-blur-sm p-8 rounded-lg border border-border/50 mb-10">
                  <div className="flex gap-5 items-start">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline text-lg">
                      5
                    </span>
                    <p className="font-serif text-foreground text-lg pt-1">Name 5 things you can <strong>see</strong> around you</p>
                  </div>
                  <div className="flex gap-5 items-start">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline text-lg">
                      4
                    </span>
                    <p className="font-serif text-foreground text-lg pt-1">Notice 4 things you can <strong>touch</strong> or feel</p>
                  </div>
                  <div className="flex gap-5 items-start">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline text-lg">
                      3
                    </span>
                    <p className="font-serif text-foreground text-lg pt-1">Identify 3 things you can <strong>hear</strong></p>
                  </div>
                  <div className="flex gap-5 items-start">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline text-lg">
                      2
                    </span>
                    <p className="font-serif text-foreground text-lg pt-1">Notice 2 things you can <strong>smell</strong></p>
                  </div>
                  <div className="flex gap-5 items-start">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline text-lg">
                      1
                    </span>
                    <p className="font-serif text-foreground text-lg pt-1">Focus on 1 thing you can <strong>taste</strong></p>
                  </div>
                </div>

                <div className="text-center">
                  <Button type="button" size="lg" className="shadow-elegant px-8" onClick={() => setShowGroundingExercise(true)}>
                    Begin Guided Exercise
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Focus Boost Details - Only show when clicked */}
        {selectedTool === "focus" && (
          <Card ref={exerciseRef} className="max-w-4xl mx-auto bg-card/80 backdrop-blur-sm border-border/50 shadow-elegant p-8 md:p-12 mb-16 scroll-mt-4">
            <div>
              <Button
                variant="ghost"
                onClick={() => setSelectedTool(null)}
                className="mb-8 gap-2 hover:bg-accent/50"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Tools
              </Button>
              
              <div>
                <h2 className="font-headline text-4xl md:text-5xl mb-6 text-foreground">
                  Memory Sequence Game
                </h2>
                <p className="font-serif text-xl text-muted-foreground leading-relaxed mb-8">
                  An interactive game designed to sharpen working memory and combat brain fog through pattern recognition.
                </p>
                
                {/* Why It Works Section */}
                <div className="bg-accent/10 border-l-4 border-primary rounded-lg p-6 md:p-8 mb-10">
                  <h3 className="font-headline text-2xl mb-4 text-foreground flex items-center gap-3">
                    <Lightbulb className="h-6 w-6 text-primary" />
                    Clinical Evidence
                  </h3>
                  <p className="font-serif text-muted-foreground leading-relaxed text-lg mb-4">
                    Brain fog affects your working memory - the mental workspace you use to hold and manipulate information. This game directly exercises your working memory through sequence repetition, forcing your brain to focus and strengthening neural pathways.
                  </p>
                  <p className="font-serif text-muted-foreground leading-relaxed text-lg">
                    The progressive difficulty provides just enough challenge to engage your prefrontal cortex (the brain's command center) without overwhelming it. Regular practice can improve concentration, mental clarity, and information processing speed - all impacted by menopause-related brain fog.
                  </p>
                </div>

                <div className="space-y-5 bg-background/50 backdrop-blur-sm p-8 rounded-lg border border-border/50 mb-10">
                  <div className="flex gap-5 items-start">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline text-lg">
                      1
                    </span>
                    <p className="font-serif text-foreground text-lg pt-1">Watch the color sequence carefully</p>
                  </div>
                  <div className="flex gap-5 items-start">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline text-lg">
                      2
                    </span>
                    <p className="font-serif text-foreground text-lg pt-1">Repeat the pattern by clicking the colors in order</p>
                  </div>
                  <div className="flex gap-5 items-start">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline text-lg">
                      3
                    </span>
                    <p className="font-serif text-foreground text-lg pt-1">Each level adds one more color to remember</p>
                  </div>
                  <div className="flex gap-5 items-start">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline text-lg">
                      4
                    </span>
                    <p className="font-serif text-foreground text-lg pt-1">Play for just 2 minutes to boost focus and clarity</p>
                  </div>
                </div>

                <div className="text-center">
                  <Button type="button" size="lg" className="shadow-elegant px-8" onClick={() => setShowFocusBoost(true)}>
                    Begin Brain Game
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Sleep Preparation Details */}
        {selectedTool === "sleep-prep" && (
          <Card ref={exerciseRef} className="max-w-4xl mx-auto bg-card/80 backdrop-blur-sm border-border/50 shadow-elegant p-8 md:p-12 mb-16 scroll-mt-4">
            <div>
              <Button
                variant="ghost"
                onClick={() => setSelectedTool(null)}
                className="mb-8 gap-2 hover:bg-accent/50"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Tools
              </Button>
              
              <div>
                <h2 className="font-headline text-4xl md:text-5xl mb-6 text-foreground">
                  Sleep Preparation Routine
                </h2>
                <p className="font-serif text-xl text-muted-foreground leading-relaxed mb-8">
                  A comprehensive 10-minute wind-down checklist to prepare your body and mind for restful sleep.
                </p>
                
                <div className="bg-accent/10 border-l-4 border-primary rounded-lg p-6 md:p-8 mb-10">
                  <h3 className="font-headline text-2xl mb-4 text-foreground flex items-center gap-3">
                    <Lightbulb className="h-6 w-6 text-primary" />
                    Clinical Evidence
                  </h3>
                  <p className="font-serif text-muted-foreground leading-relaxed text-lg mb-4">
                    Menopause disrupts sleep through hormonal changes (especially declining progesterone, nature's sedative) and night sweats. A consistent pre-sleep routine signals your brain that it's time to wind down, triggering the release of sleep hormones like melatonin.
                  </p>
                  <p className="font-serif text-muted-foreground leading-relaxed text-lg">
                    Each step addresses specific sleep disruptors: cooling the room combats night sweats, dimming lights boosts melatonin, journaling clears mental clutter, and avoiding screens prevents blue light from suppressing sleep hormones. Together, these create the optimal environment for deep, restorative sleep.
                  </p>
                </div>

                <div className="text-center">
                  <Button type="button" size="lg" className="shadow-elegant px-8" onClick={() => setShowSleepPrep(true)}>
                    Begin Sleep Routine
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* 4-7-8 Breathing Details */}
        {selectedTool === "478-breathing" && (
          <Card ref={exerciseRef} className="max-w-4xl mx-auto bg-card/80 backdrop-blur-sm border-border/50 shadow-elegant p-8 md:p-12 mb-16 scroll-mt-4">
            <div>
              <Button
                variant="ghost"
                onClick={() => setSelectedTool(null)}
                className="mb-8 gap-2 hover:bg-accent/50"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Tools
              </Button>
              
              <div>
                <h2 className="font-headline text-4xl md:text-5xl mb-6 text-foreground">
                  4-7-8 Sleep Breathing
                </h2>
                <p className="font-serif text-xl text-muted-foreground leading-relaxed mb-8">
                  Dr. Andrew Weil's proven breathing technique that can help you fall asleep in minutes.
                </p>
                
                <div className="bg-accent/10 border-l-4 border-primary rounded-lg p-6 md:p-8 mb-10">
                  <h3 className="font-headline text-2xl mb-4 text-foreground flex items-center gap-3">
                    <Lightbulb className="h-6 w-6 text-primary" />
                    Clinical Evidence
                  </h3>
                  <p className="font-serif text-muted-foreground leading-relaxed text-lg mb-4">
                    The 4-7-8 pattern acts as a natural tranquilizer for your nervous system. The extended exhale (8 seconds) activates your parasympathetic nervous system - your body's "rest and digest" mode - which directly counters the stress response that often keeps menopausal women awake.
                  </p>
                  <p className="font-serif text-muted-foreground leading-relaxed text-lg">
                    Holding your breath (7 seconds) allows oxygen to fully saturate your bloodstream and then spread throughout your body during the long exhale. This increases relaxation and reduces anxiety. With practice, this technique becomes a powerful cue that tells your body it's safe to sleep.
                  </p>
                </div>

                <div className="text-center">
                  <Button type="button" size="lg" className="shadow-elegant px-8" onClick={() => setShow478Breathing(true)}>
                    Begin 4-7-8 Breathing
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Muscle Relaxation Details */}
        {selectedTool === "muscle-relaxation" && (
          <Card ref={exerciseRef} className="max-w-4xl mx-auto bg-card/80 backdrop-blur-sm border-border/50 shadow-elegant p-8 md:p-12 mb-16 scroll-mt-4">
            <div>
              <Button
                variant="ghost"
                onClick={() => setSelectedTool(null)}
                className="mb-8 gap-2 hover:bg-accent/50"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Tools
              </Button>
              
              <div>
                <h2 className="font-headline text-4xl md:text-5xl mb-6 text-foreground">
                  Progressive Muscle Relaxation
                </h2>
                <p className="font-serif text-xl text-muted-foreground leading-relaxed mb-8">
                  A guided body scan that releases physical tension and prepares you for deep, restorative sleep.
                </p>
                
                <div className="bg-accent/10 border-l-4 border-primary rounded-lg p-6 md:p-8 mb-10">
                  <h3 className="font-headline text-2xl mb-4 text-foreground flex items-center gap-3">
                    <Lightbulb className="h-6 w-6 text-primary" />
                    Clinical Evidence
                  </h3>
                  <p className="font-serif text-muted-foreground leading-relaxed text-lg mb-4">
                    Menopause often causes joint pain, muscle tension, and a racing mind - all of which interfere with sleep. Progressive muscle relaxation (PMR) systematically releases stored tension in your body, which many women don't even realize they're carrying.
                  </p>
                  <p className="font-serif text-muted-foreground leading-relaxed text-lg">
                    By deliberately tensing then releasing each muscle group, you create a stark contrast that makes relaxation more noticeable and profound. This technique also shifts your focus from anxious thoughts to physical sensations, breaking the cycle of rumination that keeps you awake. PMR has been clinically proven to reduce insomnia and improve sleep quality.
                  </p>
                </div>

                <div className="text-center">
                  <Button type="button" size="lg" className="shadow-elegant px-8" onClick={() => setShowMuscleRelaxation(true)}>
                    Begin Muscle Relaxation
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Emergency Resources */}
        <Card className="max-w-4xl mx-auto bg-accent/5 border border-accent/20 shadow-soft p-8 md:p-10">
          <div className="flex items-start gap-6">
            <div className="p-3 rounded-full bg-accent/10 flex-shrink-0">
              <Heart className="h-7 w-7 text-accent" />
            </div>
            <div>
              <h3 className="font-headline text-2xl text-foreground mb-3">
                Need Immediate Medical Assistance?
              </h3>
              <p className="font-serif text-muted-foreground text-lg leading-relaxed mb-5">
                If you're experiencing severe symptoms or a medical emergency, please contact your healthcare provider or emergency services immediately.
              </p>
              <Button variant="outline" size="lg" className="border-accent/30 text-accent hover:bg-accent hover:text-accent-foreground shadow-soft">
                Find Local Resources
              </Button>
            </div>
          </div>
        </Card>
      </div>
      </div>
    </>
  );
};

export default SOSToolkit;
